import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { FaCar, FaSearch, FaStamp, FaShoppingCart, FaCheckCircle, FaBolt, FaHandshake, FaPhone, FaWhatsapp } from 'react-icons/fa'
import HeroCarousel from '@/components/common/HeroCarousel'

const Home = () => {
  const { t } = useTranslation()

  const carouselSlides = [
    {
      image: '/assets/Hero1.webp',
      title: 'Willkommen bei KennzeichenPro24',
      subtitle: 'Ihr Partner für Fahrzeugzulassung und Kennzeichenprägung in Göttingen',
    },
    {
      image: '/assets/Hero2.webp',
      title: 'Zulassungsservice & Wunschkennzeichen',
      subtitle: 'Schnell, sicher und unkompliziert – alles aus einer Hand',
    },
    {
      image: '/assets/Hero3.webp',
      title: 'Premium Kennzeichenprägung',
      subtitle: 'Höchste Qualität, sofort zum Mitnehmen',
    },
  ]

  const services = [
    {
      icon: <FaCar className="w-10 h-10" />,
      title: t('home.vehicleRegistration'),
      description: t('home.vehicleRegistrationDesc'),
      href: '/contact',
      gradient: 'from-blue-500 via-blue-600 to-blue-700',
      glow: 'shadow-blue-500/50',
    },
    {
      icon: <FaSearch className="w-10 h-10" />,
      title: t('home.wunschkennzeichen'),
      description: t('home.wunschkennzeichenDesc'),
      href: '/wunschkennzeichen',
      gradient: 'from-purple-500 via-purple-600 to-purple-700',
      glow: 'shadow-purple-500/50',
    },
    {
      icon: <FaStamp className="w-10 h-10" />,
      title: t('home.kennzeichenpraegung'),
      description: t('home.kennzeichenpraegungDesc'),
      href: '/contact',
      gradient: 'from-emerald-500 via-emerald-600 to-emerald-700',
      glow: 'shadow-emerald-500/50',
    },
    {
      icon: <FaShoppingCart className="w-10 h-10" />,
      title: t('home.onlineShop'),
      description: t('home.onlineShopDesc'),
      href: '/shop',
      gradient: 'from-orange-500 via-orange-600 to-orange-700',
      glow: 'shadow-orange-500/50',
    },
  ]

  const features = [
    {
      icon: <FaHandshake className="w-8 h-8" />,
      title: t('home.support24'),
      description: t('home.support24Desc'),
      delay: 0.1,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <FaBolt className="w-8 h-8" />,
      title: t('home.quality'),
      description: t('home.qualityDesc'),
      delay: 0.2,
      gradient: 'from-amber-500 to-orange-500',
    },
    {
      icon: <FaCheckCircle className="w-8 h-8" />,
      title: t('home.fastShipping'),
      description: t('home.fastShippingDesc'),
      delay: 0.3,
      gradient: 'from-emerald-500 to-green-500',
    },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  }

  return (
    <div className="bg-dark-950 min-h-screen relative overflow-hidden">
      {/* Hero Carousel */}
      <HeroCarousel slides={carouselSlides} autoPlayInterval={5000} />

      {/* Services Section */}
      <section className="relative py-16 sm:py-20 bg-dark-900/50 backdrop-blur-sm">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 sm:mb-14"
          >
            <h2 className="section-title">{t('home.services')}</h2>
            <p className="text-gray-400 mt-3 max-w-2xl mx-auto">
              Alles rund um Ihre Kfz-Zulassung – professionell, schnell und zuverlässig
            </p>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto mt-4"></div>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group"
              >
                <Link to={service.href}>
                  <div className="card-hover h-full p-5 sm:p-6 relative overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-2xl`}></div>

                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                      className={`relative inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.gradient} text-white mb-4 shadow-lg ${service.glow}`}
                    >
                      {service.icon}
                    </motion.div>

                    <h3 className="relative text-lg sm:text-xl font-bold mb-3 text-gray-100 group-hover:text-primary-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="relative text-gray-400 leading-relaxed text-sm">{service.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="relative py-16 sm:py-20 bg-dark-950">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 sm:mb-14"
          >
            <h2 className="section-title">{t('home.features')}</h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto mt-4"></div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: feature.delay, duration: 0.6 }}
                whileHover={{ scale: 1.03, y: -5 }}
                className="text-center card-hover p-8 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: feature.delay }}
                  className={`relative inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.gradient} text-white mb-4 shadow-lg`}
                >
                  {feature.icon}
                </motion.div>
                <h3 className="relative text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-100">{feature.title}</h3>
                <p className="relative text-gray-400 leading-relaxed text-xs sm:text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-20 bg-dark-900/50">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-4 text-white">
              Bereit für Ihr <span className="bg-gradient-to-r from-primary-400 to-purple-400 bg-clip-text text-transparent">Wunschkennzeichen</span>?
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Kontaktieren Sie uns oder reservieren Sie direkt online Ihr individuelles Kennzeichen.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/wunschkennzeichen"
                className="px-8 py-3.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 w-full sm:w-auto text-center"
              >
                Wunschkennzeichen prüfen
              </Link>
              <a
                href="tel:+4955149203480"
                className="px-8 py-3.5 bg-dark-800 text-gray-200 font-bold rounded-xl hover:bg-dark-700 border border-dark-600 transition-all duration-300 flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <FaPhone className="w-4 h-4" />
                <span>Jetzt anrufen</span>
              </a>
              <a
                href="https://wa.me/4915565553294"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3.5 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-green-600/25 w-full sm:w-auto"
              >
                <FaWhatsapp className="w-4 h-4" />
                <span>WhatsApp</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home
